export class MarkdownParser {
  // Reusable regex patterns
  static imagePattern = /\[image:([^\]]*)\]\(([^)]+)\)/g;
  static videoPattern = /\[video:([^\]]*)\]\(([^)]+)\)/g;
  static audioPattern = /\[audio:([^\]]*)\]\(([^)]+)\)/g;

  static hasElementTypes(content?: string, types: ('image' | 'video' | 'audio')[] = ['image', 'video', 'audio']): boolean {
    if (!content) {
      return false;
    }

    const patterns: string[] = [];

    if (types.includes('image')) {
      patterns.push(MarkdownParser.imagePattern.source);
    }
    if (types.includes('video')) {
      patterns.push(MarkdownParser.videoPattern.source);
    }
    if (types.includes('audio')) {
      patterns.push(MarkdownParser.audioPattern.source);
    }

    if (patterns.length === 0) {
      return false;
    }

    // Combine all patterns into one regex with alternation
    const combinedPattern = new RegExp(patterns.join('|'));

    return combinedPattern.test(content);
  }

  static parseMarkdown(content?: string): MarkdownParsedElement[] {
    if (!content) {
      return [{ type: 'text', content: '' }];
    }
    const elements: MarkdownParsedElement[] = [];

    // Clone patterns with global flag for exec
    const imagePattern = new RegExp(MarkdownParser.imagePattern.source, 'g');
    const videoPattern = new RegExp(MarkdownParser.videoPattern.source, 'g');
    const audioPattern = new RegExp(MarkdownParser.audioPattern.source, 'g');

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const allMatches: MarkdownMediaMatch[] = [];

    imagePattern.lastIndex = 0;
    videoPattern.lastIndex = 0;
    audioPattern.lastIndex = 0;

    while ((match = imagePattern.exec(content)) !== null) {
      allMatches.push({ type: 'image', match });
    }
    while ((match = videoPattern.exec(content)) !== null) {
      allMatches.push({ type: 'video', match });
    }
    while ((match = audioPattern.exec(content)) !== null) {
      allMatches.push({ type: 'audio', match });
    }

    allMatches.sort((a, b) => a.match.index - b.match.index);

    allMatches.forEach(({ type, match }) => {
      if (match.index > lastIndex) {
        const textContent = content.slice(lastIndex, match.index).trim();
        if (textContent) {
          elements.push({ type: 'text', content: textContent });
        }
      }

      const altText: string = match[1] || '';
      const url: string = match[2] || '';

      switch (type) {
        case 'image':
          elements.push({
            type: 'image',
            content: url,
            props: { alt: altText || 'Image' },
          });
          break;
        case 'video':
          elements.push({
            type: 'video',
            content: url,
            props: {
              title: altText || 'Video',
              alt: altText || 'Video content',
            },
          });
          break;
        case 'audio':
          elements.push({
            type: 'audio',
            content: url,
            props: {
              title: altText || 'Audio',
              alt: altText || 'Audio content',
            },
          });
          break;
      }

      lastIndex = match.index + match[0].length;
    });

    if (lastIndex < content.length) {
      const textContent = content.slice(lastIndex).trim();
      if (textContent) {
        elements.push({ type: 'text', content: textContent });
      }
    }

    if (elements.length === 0) {
      elements.push({ type: 'text', content });
    }

    return elements;
  }
}

export interface MarkdownMediaProps {
  alt?: string;
  title?: string;
}

export interface MarkdownMediaMatch {
  type: 'image' | 'video' | 'audio';
  match: RegExpExecArray;
}

export interface MarkdownParsedElement {
  type: 'text' | 'image' | 'video' | 'audio';
  content: string;
  props?: MarkdownMediaProps;
}
